import "./WaitingListView.css";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import DateFilter from "./DateFilter";
import DateGroups from "./DateGroups";
import RequestCard from "./RequestCard";
import { getNextMeetings, getNextMeetingsAsync, onMeetingsCacheUpdate, formatMeetingDateLong, formatMeetingDateTime, toIsoDate } from "../utils/dateHelpers";
import {
  watchOpenRequests,
  watchAcceptedRequests,
  watchAllRequestsForCounts,
  watchAllAcceptedForCounts,
  deleteRequest,
  cancelDriverHelp,
  updateRequest,
} from "../services/requests";
import RequestNearbyDriversModal from "./RequestNearbyDriversModal";
import BackBar from "./BackBar";
import { removeMyRequest } from "../services/myRequests";
import ConfirmDialog from "./ConfirmDialog";
import NavigationModal from "./NavigationModal";
import { watchDriverOffers } from "../services/firebaseApi";
import { getEventColorForDate, getColorWithOpacity } from "../utils/eventColorHelper";
import Chat from "./Chat";
import IdentitySelector from "./IdentitySelector";
import { getChatId } from "../services/chatService";
import { hasMessages } from "../services/chatService";
import UpdateRequestModal from "./UpdateRequestModal";

// Calcule une carte de surlignage (orange/rouge) pour la prochaine réunion.
// - "closest" (orange) = prochaine date future (toujours colorée)
// - "eve" (rouge) = depuis 00:00 la veille du jour de réunion jusqu'à l'heure de la réunion
const calculateHighlightMap = (options, pendingRequests) => {
  const DAY = 24 * 60 * 60 * 1000;
  const now = Date.now();

  console.log("🔍 [calculateHighlightMap] Debug:", {
    totalRequests: pendingRequests?.length || 0,
    now: new Date(now).toISOString(),
    optionsCount: options?.length || 0
  });

  // Trouver la prochaine réunion (toutes les dates futures, pas seulement celles avec des demandes en attente)
  const allFutureOptions = (options || []).filter((o) => {
    if (!o || !o.isoDate) return false;
    const meetingTs = o.meetingAt || o.value;
    return typeof meetingTs === "number" && meetingTs >= now;
  });

  console.log("📅 Toutes les dates futures:", allFutureOptions.length);

  if (allFutureOptions.length === 0) {
    console.log("❌ Aucune date future trouvée");
    return new Map();
  }

  // Trier par date et prendre la plus proche
  const sorted = allFutureOptions
    .map((o) => o.meetingAt || o.value)
    .filter((ts) => typeof ts === "number")
    .sort((a, b) => a - b);

  const closestTs = sorted[0]; // La plus proche
  console.log("🎯 Prochaine réunion:", new Date(closestTs).toISOString());

  // Vérifier s'il y a des demandes en attente pour cette date
  const onlyPending = (pendingRequests || []).filter((r) => {
    if (!r) return false;
    const seatsAssigned = Number(r.seatsAssigned || 0);
    const seatsNeeded = Number(r.seatsNeeded || 0);
    const seatsRemaining = Number(
      typeof r.seatsRemaining === 'number'
        ? r.seatsRemaining
        : Math.max(0, seatsNeeded - seatsAssigned)
    );
    return r.assigned !== true || seatsRemaining > 0;
  });

  const closestIsoDate = new Date(closestTs).toISOString().split("T")[0];
  const hasPendingForClosest = onlyPending.some((r) => {
    const requestIsoDate = r.isoDate || new Date(r.meetingAt).toISOString().split("T")[0];
    return requestIsoDate === closestIsoDate;
  });

  console.log("📊 Demandes en attente pour la prochaine réunion:", hasPendingForClosest);

  // Fenêtre rouge: [00:00 la veille, heure de réunion)
  const meetingDate = new Date(closestTs);
  const meetingStartOfDay = new Date(meetingDate.getFullYear(), meetingDate.getMonth(), meetingDate.getDate());
  const eveStart = meetingStartOfDay.getTime() - DAY; // 00:00 la veille
  const isInEveWindow = now >= eveStart && now < closestTs;

  console.log("🔴 Fenêtre rouge:", {
    eveStart: new Date(eveStart).toISOString(),
    now: new Date(now).toISOString(),
    closestTs: new Date(closestTs).toISOString(),
    isInEveWindow
  });

  const map = new Map();
  // Toujours colorer la prochaine réunion, même si toutes les demandes sont acceptées
  map.set(closestTs, isInEveWindow ? "eve" : "closest");
  
  console.log("🎨 Carte de coloration finale:", map);
  return map;
};

const WaitingListView = ({ onBack, onGoToDriverForm }) => {
  const [selectedMeetingAt, setSelectedMeetingAt] = useState(null);
  const [requests, setRequests] = useState([]);
  const [acceptedRequests, setAcceptedRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]); // Données complètes pour les compteurs
  const [allAcceptedRequests, setAllAcceptedRequests] = useState([]); // Données complètes pour les compteurs
  const [driverOffers, setDriverOffers] = useState([]); // Offres conducteurs pour compteur discret
  const [loading, setLoading] = useState(true);
  const [activeRequest, setActiveRequest] = useState(null);
  const [activeTab, setActiveTab] = useState("pending"); // 'pending' ou 'accepted'
  const [openChat, setOpenChat] = useState(null); // { requestId, driverName, passengerName }
  const [currentUserName, setCurrentUserName] = useState(null);
  const [showIdentitySelector, setShowIdentitySelector] = useState(null);
  const [chatsWithMessages, setChatsWithMessages] = useState(new Set());

  // Refs pour le geste de balayage
  const touchStartXRef = useRef(null);
  const touchStartYRef = useRef(null);

  // État pour la modale globale de confirmation
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [requestToEdit, setRequestToEdit] = useState(null);
  const [isSavingRequest, setIsSavingRequest] = useState(false);
  const [isDeletingRequest, setIsDeletingRequest] = useState(false);

  // État pour la confirmation d'annulation de l'aide
  const [confirmCancelHelpOpen, setConfirmCancelHelpOpen] = useState(false);
  const [requestToCancelHelp, setRequestToCancelHelp] = useState(null);

  const [options, setOptions] = useState(() => {
    const base = [{ label: "Toutes", value: null, isoDate: null }];
    const meetings = getNextMeetings(8);
    const next = meetings.map((x) => ({
      meetingAt: x.meetingAt,
      label: x.isSpecial ? (x.labelSimple || x.label) : x.label,
      value: x.meetingAt,
      isoDate: x.isoDate,
      isSpecial: x.isSpecial,
      specialType: x.specialType,
      color: x.color,
    }));
    return [...base, ...next];
  });

  // Charger les données depuis Firebase et écouter les mises à jour
  useEffect(() => {
    const updateOptions = async () => {
      try {
        const meetings = await getNextMeetingsAsync(8);
        const base = [{ label: "Toutes", value: null, isoDate: null }];
        const next = meetings.map((x) => ({
          meetingAt: x.meetingAt,
          label: x.isSpecial ? (x.labelSimple || x.label) : x.label,
          value: x.meetingAt,
          isoDate: x.isoDate,
          isSpecial: x.isSpecial,
          specialType: x.specialType,
          color: x.color,
        }));
        setOptions([...base, ...next]);
      } catch (error) {
        console.warn('⚠️ [WaitingListView] Erreur chargement async meetings:', error);
      }
    };

    updateOptions();

    // Écouter les mises à jour du cache réactif
    const unsubscribe = onMeetingsCacheUpdate(async () => {
      console.log('🔄 [WaitingListView] Cache mis à jour, rechargement...');
      await updateOptions();
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Highlight basé sur les demandes en attente réelles
  const highlightMap = useMemo(
    () => calculateHighlightMap(options, requests),
    [options, requests],
  );

  // Fonction sécurisée pour mettre à jour les requêtes
  const safeSetRequests = useCallback((newRequests) => {
    try {
      // Vérifier que newRequests est un tableau valide
      if (Array.isArray(newRequests)) {
        // Filtrer les éléments invalides et s'assurer que chaque requête a un ID
        const validRequests = newRequests.filter((r) => r && r.id);
        const uniqueRequests = validRequests.filter(
          (r, index, arr) => arr.findIndex((req) => req.id === r.id) === index,
        );
        setRequests(uniqueRequests);
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour des requêtes:", error);
      // En cas d'erreur, on garde l'état précédent
    }
  }, []);

  // Fonction sécurisée pour mettre à jour les requêtes acceptées
  const safeSetAcceptedRequests = useCallback((newAcceptedRequests) => {
    try {
      if (Array.isArray(newAcceptedRequests)) {
        const validRequests = newAcceptedRequests.filter(
          (r) => r && r.id && typeof r.id === "string",
        );
        const uniqueRequests = validRequests.filter(
          (r, index, arr) => arr.findIndex((req) => req.id === r.id) === index,
        );
        setAcceptedRequests(uniqueRequests);
      }
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour des requêtes acceptées:",
        error,
      );
    }
  }, []);

  // Fonction sécurisée pour mettre à jour toutes les requêtes (pour les compteurs)
  const safeSetAllRequests = useCallback((newAllRequests) => {
    try {
      if (Array.isArray(newAllRequests)) {
        const validRequests = newAllRequests.filter(
          (r) => r && r.id && typeof r.id === "string",
        );
        const uniqueRequests = validRequests.filter(
          (r, index, arr) => arr.findIndex((req) => req.id === r.id) === index,
        );
        setAllRequests(uniqueRequests);
      }
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour de toutes les requêtes:",
        error,
      );
    }
  }, []);

  // Fonction sécurisée pour mettre à jour toutes les requêtes acceptées (pour les compteurs)
  const safeSetAllAcceptedRequests = useCallback((newAllAcceptedRequests) => {
    try {
      if (Array.isArray(newAllAcceptedRequests)) {
        const validRequests = newAllAcceptedRequests.filter(
          (r) => r && r.id && typeof r.id === "string",
        );
        const uniqueRequests = validRequests.filter(
          (r, index, arr) => arr.findIndex((req) => req.id === r.id) === index,
        );
        setAllAcceptedRequests(uniqueRequests);
      }
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour de toutes les requêtes acceptées:",
        error,
      );
    }
  }, []);

  useEffect(() => {
    console.log("🚨 WaitingListView useEffect DÉMARRÉ - surveillance de toutes les demandes");
    setLoading(true);

    // Surveillance de TOUTES les demandes (plus simple !)
    const unsubscribePending = watchAllRequestsForCounts(
      (rows) => {
        console.log("📊 [WaitingListView] Toutes les demandes reçues:", rows?.length || 0);
        console.log("📊 [WaitingListView] Détails des demandes en attente:", rows?.map(r => ({
          id: r.id,
          name: r.name || r.passengerName,
          isoDate: r.isoDate,
          meetingAt: r.meetingAt ? new Date(r.meetingAt).toISOString() : null,
          seatsAssigned: r.seatsAssigned,
          seatsNeeded: r.seatsNeeded,
          seatsRemaining: r.seatsRemaining
        })));
        safeSetAllRequests(rows);
        setLoading(false);
      },
    );

    const unsubscribeAccepted = watchAllAcceptedForCounts(
      (rows) => {
        console.log("📊 [WaitingListView] Toutes les demandes acceptées reçues:", rows?.length || 0);
        console.log("📊 [WaitingListView] Détails des demandes acceptées:", rows?.map(r => ({
          id: r.id,
          name: r.name || r.passengerName,
          isoDate: r.isoDate,
          meetingAt: r.meetingAt ? new Date(r.meetingAt).toISOString() : null,
          seatsAssigned: r.seatsAssigned,
          seatsNeeded: r.seatsNeeded,
          drivers: r.drivers ? Object.keys(r.drivers) : []
        })));
        safeSetAllAcceptedRequests(rows);
      },
    );

    return () => {
      console.log("🔄 [WaitingListView] Nettoyage des watchers");
      unsubscribePending && unsubscribePending();
      unsubscribeAccepted && unsubscribeAccepted();
    };
  }, [safeSetAllRequests, safeSetAllAcceptedRequests]);

  // Watch offres conducteurs pour les compteurs discrets
  useEffect(() => {
    try {
      const unsubscribe = watchDriverOffers((rows) => {
        setDriverOffers(Array.isArray(rows) ? rows : []);
      });
      return () => { try { unsubscribe && unsubscribe(); } catch {}
      };
    } catch (e) {
      console.warn("⚠️ watchDriverOffers indisponible:", e);
    }
  }, []);

  // Charger le nom de l'utilisateur actuel depuis localStorage
  useEffect(() => {
    const storedName = localStorage.getItem("cvtrg_current_user");
    if (storedName) {
      setCurrentUserName(storedName);
    }
  }, []);

  // Vérifier s'il y a des messages pour les demandes acceptées
  useEffect(() => {
    if (activeTab !== "accepted" || filteredAcceptedRequests.length === 0) {
      setChatsWithMessages(new Set());
      return;
    }

    const unsubscribes = [];

    filteredAcceptedRequests.forEach((request) => {
      const drivers = request.drivers ? Object.keys(request.drivers) : (request.driverName ? [request.driverName] : []);
      const passengerName = request.name || request.passengerName || "Nom non renseigné";

      drivers.forEach((driverKey) => {
        const normalizeKey = (name) => String(name || '').replace(/[.#$/\[\]]/g, '_').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
        const driverData = request.drivers ? (request.drivers[driverKey] || request.drivers[normalizeKey(driverKey)]) : null;
        const driverName = driverData?.driverName || driverKey;
        const chatId = getChatId(request.id, driverName, passengerName);
        
        const unsubscribe = hasMessages(chatId, (hasMsgs) => {
          setChatsWithMessages(prev => {
            const newSet = new Set(prev);
            if (hasMsgs) {
              newSet.add(chatId);
            } else {
              newSet.delete(chatId);
            }
            return newSet;
          });
        });
        unsubscribes.push(unsubscribe);
      });
    });

    return () => {
      unsubscribes.forEach(unsub => unsub && unsub());
    };
  }, [activeTab, allAcceptedRequests, selectedMeetingAt]);

  // Gérer la sélection d'identité
  const handleIdentitySelect = (selectedName, chatInfo) => {
    setCurrentUserName(selectedName);
    localStorage.setItem("cvtrg_current_user", selectedName);
    setShowIdentitySelector(null);
    setOpenChat({
      requestId: chatInfo.requestId,
      driverName: chatInfo.driverName,
      passengerName: chatInfo.passengerName,
    });
  };

  // Marquer un chat comme lu quand on le ferme
  const handleChatOpened = (requestId, driverName, passengerName) => {
    const chatId = getChatId(requestId, driverName, passengerName);
    // Note: markChatAsReadLocal n'est pas exporté, on peut l'ignorer pour l'instant
    setOpenChat(null);
  };

  // Gestion du balayage horizontal (web mobile)
  const handleTouchStart = useCallback((e) => {
    const t = e.changedTouches?.[0];
    if (!t) return;
    touchStartXRef.current = t.clientX;
    touchStartYRef.current = t.clientY;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - (touchStartXRef.current ?? t.clientX);
    const dy = t.clientY - (touchStartYRef.current ?? t.clientY);
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    // Seuils: glissement principalement horizontal et distance suffisante
    if (absDx > 40 && absDy < 30) {
      if (dx < 0) {
        // vers la gauche → aller vers acceptées
        setActiveTab("accepted");
      } else {
        // vers la droite → revenir à en attente
        setActiveTab("pending");
      }
    }
    touchStartXRef.current = null;
    touchStartYRef.current = null;
  }, []);

  // Surveillance des données complètes pour les compteurs (toujours actif)
  useEffect(() => {
    console.log(
      "🔍 Démarrage de la surveillance des données complètes pour les compteurs",
    );

    const unsubscribeAllPending = watchAllRequestsForCounts(
      (rows) => {
        console.log(
          "📊 watchAllRequestsForCounts - Données reçues:",
          rows?.length || 0,
        );
        safeSetAllRequests(rows);
      },
      (err) => console.error("watch error:", err),
    );

    const unsubscribeAllAccepted = watchAllAcceptedForCounts(
      (rows) => {
        console.log(
          "📊 watchAllAcceptedForCounts - Données reçues:",
          rows?.length || 0,
        );
        safeSetAllAcceptedRequests(rows);
      },
      (err) => console.error("watch error:", err),
    );

    return () => {
      console.log(
        "🔍 Arrêt de la surveillance des données complètes pour les compteurs",
      );
      unsubscribeAllPending && unsubscribeAllPending();
      unsubscribeAllAccepted && unsubscribeAllAccepted();
    };
  }, [safeSetAllRequests, safeSetAllAcceptedRequests]);

  // Handler pour ouvrir la modale de modification
  const handleAskEdit = useCallback((request) => {
    if (request && request.id) {
      setRequestToEdit(request);
    }
  }, []);

  const handleSaveEdit = useCallback(
    async (payload) => {
      if (!requestToEdit?.id) return;
      try {
        setIsSavingRequest(true);
        await updateRequest(requestToEdit.id, payload);
        setRequestToEdit(null);
        alert("Demande modifiée avec succès.");
      } catch (error) {
        console.error("Erreur lors de la modification:", error);
        alert("Impossible de modifier la demande. Veuillez réessayer.");
      } finally {
        setIsSavingRequest(false);
      }
    },
    [requestToEdit],
  );

  const handleDeleteFromEditModal = useCallback(() => {
    if (!requestToEdit?.id) return;
    setRequestToDelete(requestToEdit);
    setRequestToEdit(null);
    setConfirmOpen(true);
  }, [requestToEdit]);

  // Handler pour demander l'annulation de l'aide
  const handleAskCancelHelp = useCallback((request) => {
    if (request && request.id) {
      setRequestToCancelHelp(request);
      setConfirmCancelHelpOpen(true);
    }
  }, []);

  // Handler pour confirmer l'annulation de l'aide
  const handleConfirmCancelHelp = useCallback(async (opts = {}) => {
    if (!requestToCancelHelp) return;

    try {
      const result = await cancelDriverHelp({
        requestId: requestToCancelHelp.id,
        driverName: opts.driverName || requestToCancelHelp.driverName,
      });

      if (result.ok) {
        // La demande sera automatiquement mise à jour via Firebase
        setConfirmCancelHelpOpen(false);
        setRequestToCancelHelp(null);
        alert(
          "Aide annulée avec succès. La demande est maintenant disponible pour d'autres conducteurs.",
        );
      } else {
        alert("Erreur lors de l'annulation. Veuillez réessayer.");
      }
    } catch (error) {
      console.error("Erreur lors de l'annulation de l'aide:", error);
      alert("Une erreur est survenue lors de l'annulation.");
    }
  }, [requestToCancelHelp]);

  // Comptage simple et direct
  const simpleCounts = useMemo(() => {
    console.log(`🔍 [simpleCounts] DÉBUT CALCUL - allRequests: ${allRequests?.length || 0}, allAcceptedRequests: ${allAcceptedRequests?.length || 0}`);
    
    const counts = {};

    // Compteur "Toutes"
    counts["all"] = (allRequests?.length || 0) + (allAcceptedRequests?.length || 0);

    // Compter directement par date
    if (allRequests && allRequests.length > 0) {
      console.log(`📊 [simpleCounts] Traitement de ${allRequests.length} demandes en attente`);
      allRequests.forEach((request, index) => {
        const isoDate = request.isoDate || (request.meetingAt ? new Date(request.meetingAt).toISOString().split("T")[0] : null);
        if (isoDate) {
          counts[isoDate] = (counts[isoDate] || 0) + 1;
          console.log(`📊 [simpleCounts] [${index + 1}/${allRequests.length}] Demande en attente ${request.id} (${request.name || request.passengerName}) → date: ${isoDate}`);
        } else {
          console.log(`⚠️ [simpleCounts] [${index + 1}/${allRequests.length}] Demande en attente ${request.id} (${request.name || request.passengerName}) SANS DATE`, {
            hasIsoDate: !!request.isoDate,
            hasMeetingAt: !!request.meetingAt,
            meetingAtValue: request.meetingAt,
            meetingAtType: typeof request.meetingAt
          });
        }
      });
    } else {
      console.log(`⚠️ [simpleCounts] allRequests est vide ou undefined`);
    }

    if (allAcceptedRequests && allAcceptedRequests.length > 0) {
      console.log(`📊 [simpleCounts] Traitement de ${allAcceptedRequests.length} demandes acceptées`);
      allAcceptedRequests.forEach((request, index) => {
        const isoDate = request.isoDate || (request.meetingAt ? new Date(request.meetingAt).toISOString().split("T")[0] : null);
        if (isoDate) {
          counts[isoDate] = (counts[isoDate] || 0) + 1;
          console.log(`📊 [simpleCounts] [${index + 1}/${allAcceptedRequests.length}] Demande acceptée ${request.id} (${request.name || request.passengerName}) → date: ${isoDate}`);
        } else {
          console.log(`⚠️ [simpleCounts] [${index + 1}/${allAcceptedRequests.length}] Demande acceptée ${request.id} SANS DATE`, {
            hasIsoDate: !!request.isoDate,
            hasMeetingAt: !!request.meetingAt,
            meetingAtValue: request.meetingAt
          });
        }
      });
    } else {
      console.log(`⚠️ [simpleCounts] allAcceptedRequests est vide ou undefined`);
    }

    console.log("🎯 [simpleCounts] Compteurs finaux:", counts);
    return counts;
  }, [allRequests, allAcceptedRequests]);

  // Compteurs d'offres par date (isoDate)
  const offerCounts = useMemo(() => {
    const map = {};
    (driverOffers || []).forEach((o) => {
      const iso = o?.isoDate || (o?.meetingAt ? new Date(o.meetingAt).toISOString().split("T")[0] : null);
      if (!iso) return;
      map[iso] = (map[iso] || 0) + 1;
    });
    return map;
  }, [driverOffers]);

  // Compteurs totaux
  const totalCount = allRequests.length + allAcceptedRequests.length;
  const acceptedCount = allAcceptedRequests.length;

  console.log(
    "🔍 Compteurs finaux - totalCount:",
    totalCount,
    "acceptedCount:",
    acceptedCount,
  );
  console.log("🔍 Compteurs par date:", simpleCounts);

  // Forcer la mise à jour de l'interface quand les compteurs changent
  useEffect(() => {
    console.log("🔄 Interface mise à jour - simpleCounts:", simpleCounts);
  }, [simpleCounts]);

  // Filtrage SIMPLE côté React !
  const filteredRequests = useMemo(() => {
    if (!selectedMeetingAt || !allRequests) return allRequests || [];

    const selectedIso = typeof selectedMeetingAt === "number"
      ? new Date(selectedMeetingAt).toISOString().split("T")[0]
      : selectedMeetingAt;

    console.log(`🔍 [WaitingListView] Filtrage simple pour la date: ${selectedIso}`);

    const filtered = allRequests.filter((request) => {
      const requestIsoDate = request.isoDate || new Date(request.meetingAt).toISOString().split("T")[0];
      return requestIsoDate === selectedIso;
    });

    console.log(`✅ [WaitingListView] Filtrage: ${filtered.length} demandes sur ${allRequests.length} total`);
    return filtered;
  }, [selectedMeetingAt, allRequests]);

  const filteredAcceptedRequests = useMemo(() => {
    console.log(`🔍 [WaitingListView] filteredAcceptedRequests - selectedMeetingAt:`, selectedMeetingAt);
    console.log(`🔍 [WaitingListView] filteredAcceptedRequests - allAcceptedRequests:`, allAcceptedRequests?.length || 0);
    
    if (!selectedMeetingAt || !allAcceptedRequests) {
      console.log(`✅ [WaitingListView] Pas de filtre, retour de toutes les demandes acceptées: ${allAcceptedRequests?.length || 0}`);
      return allAcceptedRequests || [];
    }

    const selectedIso = typeof selectedMeetingAt === "number"
      ? new Date(selectedMeetingAt).toISOString().split("T")[0]
      : selectedMeetingAt;

    console.log(`🔍 [WaitingListView] Filtrage par date ISO: ${selectedIso}`);

    const filtered = allAcceptedRequests.filter((request) => {
      const requestIsoDate = request.isoDate || new Date(request.meetingAt).toISOString().split("T")[0];
      const matches = requestIsoDate === selectedIso;
      
      const requestName = request.name || request.passengerName || 'Sans nom';
      const requestId = request.id || 'Sans ID';
      const meetingAtStr = request.meetingAt ? new Date(request.meetingAt).toISOString() : 'null';
      
      console.log(`🔍 [WaitingListView] Demande ${requestId} - ${requestName}`);
      console.log(`   Date ISO demande: ${requestIsoDate}`);
      console.log(`   Date ISO sélectionnée: ${selectedIso}`);
      console.log(`   Correspond: ${matches}`);
      console.log(`   meetingAt: ${meetingAtStr}`);
      
      return matches;
    });

    console.log(`✅ [WaitingListView] Demandes acceptées filtrées: ${filtered.length} sur ${allAcceptedRequests.length} total`);
    
    // Afficher les détails de manière sécurisée
    filtered.forEach((r, index) => {
      const name = r.name || r.passengerName || 'Sans nom';
      const id = r.id || 'Sans ID';
      const drivers = r.drivers ? Object.keys(r.drivers).join(', ') : 'Aucun';
      const seatsAssigned = r.seatsAssigned || 0;
      const seatsNeeded = r.seatsNeeded || 1;
      console.log(`   ${index + 1}. ${name} (${id}) - Conducteurs: [${drivers}] - Places: ${seatsAssigned}/${seatsNeeded}`);
    });
    
    return filtered;
  }, [selectedMeetingAt, allAcceptedRequests]);

  // Debug: Afficher le nombre de demandes filtrées
  console.log(`🔍 [WaitingListView] DateGroups va recevoir ${filteredRequests?.length || 0} demandes filtrées pour la date ${selectedMeetingAt}`);

  return (
    <div onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      <BackBar onBack={onBack} offsetTopPx={72} showLabel={false}>
        <div className="top-tabs-card">
          <div className="top-tabs" role="tablist" aria-label="Navigation demandes">
          {/* Colonne des onglets à droite */}
          <div className="top-pills-col">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "pending"}
              className={`top-pill ${activeTab === "pending" ? "is-active" : ""}`}
              onClick={() => setActiveTab("pending")}
            >
              <span className="pill-icon" aria-hidden>⌛</span>
              <span className="pill-label">En attente</span>
              <span className="pill-count">{filteredRequests?.length || 0}</span>
            </button>

            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "accepted"}
              className={`top-pill ${activeTab === "accepted" ? "is-active" : ""}`}
              onClick={() => setActiveTab("accepted")}
            >
              <span className="pill-icon" aria-hidden>✅</span>
              <span className="pill-label">Acceptées</span>
              <span className="pill-count">{filteredAcceptedRequests?.length || 0}</span>
            </button>
          </div>
        </div>
        </div>
      </BackBar>

      <div className="waiting-list-view" style={{ marginTop: "16px" }}>
        <div className="sidebar">
          <div className="tabs-bar">
            <div className="pills-row">
              {console.log("🎯 Rendu DateFilter avec counts:", simpleCounts)}
              {options.map((opt) => (
                <DateFilter
                  key={opt.value ?? opt.label}
                  options={[opt]}
                  value={selectedMeetingAt}
                  onChange={setSelectedMeetingAt}
                  counts={simpleCounts}
                  offerCounts={offerCounts}
                  highlightMap={highlightMap}
                  containerClassName="pills-row"
                />
              ))}
            </div>
          </div>
        </div>

        <div className="content-main">
          {loading ? (
            <div>
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="animate-fade-in-up"
                  style={{ marginBottom: 18 }}
                >
                  <div
                    style={{
                      height: 16,
                      width: 220,
                      background: "#f3f4f6",
                      borderRadius: 4,
                      marginBottom: 12,
                    }}
                  />
                  {[1, 2].map((j) => (
                    <div
                      key={j}
                      style={{
                        height: 64,
                        background: "#9fafb",
                        border: "1px solid #f3f4f6",
                        borderRadius: 12,
                        marginBottom: 8,
                      }}
                    />
                  ))}
                </div>
              ))}
            </div>
          ) : activeTab === "pending" ? (
            <DateGroups
              requests={filteredRequests}
              onTakeClick={setActiveRequest}
              onAskEdit={handleAskEdit}
              highlightMap={highlightMap}
              selectedMeetingAt={selectedMeetingAt}
            />
          ) : (
            <AcceptedRequestsView
              requests={filteredAcceptedRequests}
              onCancelHelp={handleAskCancelHelp}
              onOpenChat={(requestId, driverName, passengerName) => {
                setShowIdentitySelector({ requestId, driverName, passengerName });
              }}
              chatsWithMessages={chatsWithMessages}
            />
          )}
        </div>
      </div>

      {activeRequest && (
        <RequestNearbyDriversModal
          request={activeRequest}
          onClose={() => setActiveRequest(null)}
          onGoToDriverForm={onGoToDriverForm}
        />
      )}

      {/* Modale globale de confirmation */}
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setRequestToDelete(null);
        }}
        title="Confirmer la suppression"
        message={requestToDelete ? (
          <div>
            Voulez-vous vraiment annuler cette demande ?
            <div className="text-xs text-gray-500 mt-2">
              {requestToDelete.name || requestToDelete.passengerName} — {requestToDelete.meetingAt
            ? formatMeetingDateTime(requestToDelete.meetingAt)
                : "Date non précisée"}
            </div>
          </div>
        ) : null}
        cancelLabel="Annuler"
        confirmLabel="Oui, supprimer"
        onConfirm={async () => {
          if (!requestToDelete || !requestToDelete.id) return;
          const requestId = requestToDelete.id;
          try {
            setIsDeletingRequest(true);
            setConfirmOpen(false);
            setRequestToDelete(null);
            await deleteRequest(requestId);
            removeMyRequest(requestId);
            alert("Demande supprimée avec succès.");
          } catch (error) {
            console.error("Erreur lors de la suppression:", error);
            alert("Une erreur est survenue lors de la suppression. Veuillez réessayer.");
          } finally {
            setIsDeletingRequest(false);
          }
        }}
      />

      <UpdateRequestModal
        open={Boolean(requestToEdit)}
        request={requestToEdit}
        onClose={() => setRequestToEdit(null)}
        onSave={handleSaveEdit}
        onDelete={handleDeleteFromEditModal}
        saving={isSavingRequest}
        deleting={isDeletingRequest}
      />

      {/* Modale de confirmation pour annuler l'aide */}
      <ConfirmDialog
        open={confirmCancelHelpOpen}
        onClose={() => {
          setConfirmCancelHelpOpen(false);
          setRequestToCancelHelp(null);
        }}
        title="Qui êtes-vous ?"
        message={
          requestToCancelHelp ? (
            <div>
              Annuler l'aide pour {requestToCancelHelp.name || requestToCancelHelp.passengerName} — {" "}
              {requestToCancelHelp.meetingAt
                ? formatMeetingDateTime(requestToCancelHelp.meetingAt)
                : "Date non précisée"}
            </div>
          ) : null
        }
        actions={
          requestToCancelHelp ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {Object.keys(requestToCancelHelp.drivers || {}).map((driverName) => (
                  <button
                    key={`driver-${driverName}`}
                    type="button"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      await handleConfirmCancelHelp({ driverName });
                    }}
                    style={{
                      flex: 1,
                      padding: "14px 16px",
                      minHeight: "44px",
                      borderRadius: 8,
                      border: "1px solid #d1d5db",
                      background: "#f3f4f6",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: "15px",
                      touchAction: "manipulation",
                      WebkitTapHighlightColor: "transparent",
                      userSelect: "none",
                    }}
                    aria-label={`Confirmer que je suis ${driverName}`}
                  >
                    {driverName}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setConfirmCancelHelpOpen(false);
                  }}
                  style={{
                    flex: 1,
                    padding: "14px 16px",
                    minHeight: "44px",
                    borderRadius: 8,
                    border: "1px solid #d1d5db",
                    background: "white",
                    cursor: "pointer",
                    fontSize: "15px",
                    fontWeight: "500",
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent",
                    userSelect: "none",
                  }}
                  aria-label="Fermer"
                >
                  Fermer
                </button>
              </div>
            </div>
          ) : null
        }
      />

      {/* Sélecteur d'identité */}
      {showIdentitySelector && (
        <IdentitySelector
          driverName={showIdentitySelector.driverName}
          passengerName={showIdentitySelector.passengerName}
          onSelect={(selectedName) => handleIdentitySelect(selectedName, showIdentitySelector)}
          onClose={() => setShowIdentitySelector(null)}
        />
      )}

      {/* Composant Chat */}
      {openChat && currentUserName && (() => {
        const request = filteredAcceptedRequests.find(r => r.id === openChat.requestId);
        return (
          <Chat
            isOpen={true}
            onClose={() => {
              handleChatOpened(openChat.requestId, openChat.driverName, openChat.passengerName);
              setOpenChat(null);
            }}
            requestId={openChat.requestId}
            driverName={openChat.driverName}
            passengerName={openChat.passengerName}
            currentUserName={currentUserName}
            description={request?.description}
          />
        );
      })()}
    </div>
  );
};

const AcceptedRequestsView = ({ requests, onCancelHelp, onOpenChat, chatsWithMessages = new Set() }) => {
  const [navigationModal, setNavigationModal] = useState({
    isOpen: false,
    address: "",
  });
  const [eventColors, setEventColors] = useState(new Map()); // Map: requestId -> color

  // Charger les couleurs des événements
  useEffect(() => {
    const loadEventColors = async () => {
      const colorsMap = new Map();
      for (const r of requests) {
        if (r.meetingAt) {
          const isoDate = r.isoDate || toIsoDate(r.meetingAt);
          const color = await getEventColorForDate(isoDate);
          if (color) {
            colorsMap.set(r.id, color);
          }
        }
      }
      setEventColors(colorsMap);
    };
    if (requests && requests.length > 0) {
      loadEventColors();
    }
  }, [requests]);

  if (!requests || requests.length === 0) {
    return (
      <div
        className="group-card empty-state animate-fade-in-up"
        style={{ textAlign: "center", padding: "24px 12px", color: "#6b7280" }}
      >
        <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
        <div>Aucune demande acceptée pour la période sélectionnée</div>
      </div>
    );
  }

  const handleNavigationClick = (request) => {
    const address = [request.address, request.postalCode, request.city]
      .filter(Boolean)
      .join(", ");
    setNavigationModal({ isOpen: true, address });
  };

  const formatTime = (ts) => {
    const d = ts ? new Date(ts) : null;
    if (!d) return "";
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  const formatDate = (ts) => {
    if (typeof ts !== "number") return "";
    return formatMeetingDateLong(ts);
  };

  const shortAddress = (r) => {
    const street = (r.address || "").split(",")[0].trim();
    const city = r.city || "";
    return [street, city].filter(Boolean).join(", ");
  };

  const placesBadge = (r) => {
    const assigned = Number(r.seatsAssigned || 0);
    const needed = Number(r.seatsNeeded || assigned || 1);
    const remaining = Math.max(0, needed - assigned);
    const ratio = `${assigned}/${needed}`;
    const DAY = 24*60*60*1000; const now = Date.now(); const ts = Number(r.meetingAt || 0);
    const d = new Date(ts); const sod = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const eveStart = sod - DAY; const inEveWindow = now >= eveStart && now < ts;
    let bg = "#dcfce7", fg = "#065f46"; // vert par défaut
    if (remaining > 0 && inEveWindow) { bg = "#fef3c7"; fg = "#92400e"; } // ambre si incomplet veille
    if (remaining > 0 && now >= ts) { bg = "#fee2e2"; fg = "#b91c1c"; } // rouge si l’heure est passée (sécurité)
    return (
      <span style={{ background:bg, color:fg, borderRadius:9999, padding:"4px 10px", fontWeight:700 }}>
        {remaining > 0 && (inEveWindow || now >= ts) ? "⚠️ " : ""}{ratio}
      </span>
    );
  };

  console.log(`🔍 [AcceptedRequestsView] Reçoit ${requests?.length || 0} demandes acceptées`);
  
  return (
    <>
      <div className="space-y-3">
        {requests.map((r, index) => {
          console.log(`🔍 [AcceptedRequestsView] Traitement demande ${index + 1}/${requests.length}:`, r.id, r.name || r.passengerName);
          return (
            <div
              key={r.id}
              className="animate-fade-in-up"
              style={{
                animationDelay: `${index * 40}ms`,
                background: eventColors.get(r.id) ? getColorWithOpacity(eventColors.get(r.id), 0.15) : "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 14,
                boxShadow: "0 1px 2px rgba(0,0,0,.06)",
                padding: 12,
              }}
            >
              {/* Header compact avec chat à gauche */}
              <div style={{ display:'grid', gridTemplateColumns:'auto 1fr auto', alignItems:'center', gap:8 }}>
                {/* Bouton chat à gauche */}
                {(() => {
                  const drivers = r.drivers ? Object.keys(r.drivers) : (r.driverName ? [r.driverName] : []);
                  if (drivers.length === 0 || !onOpenChat) return null;
                  
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {drivers.map((dname) => {
                        const normalizeKey = (name) => String(name || '').replace(/[.#$/\[\]]/g, '_').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
                        const driverKey = normalizeKey(dname);
                        const driverData = r.drivers ? (r.drivers[driverKey] || r.drivers[dname]) : null;
                        const actualDriverName = driverData?.driverName || dname;
                        const passengerName = r.name || r.passengerName;
                        const chatId = getChatId(r.id, actualDriverName, passengerName);
                        const hasMsgs = chatsWithMessages.has(chatId);
                        
                        return (
                          <button
                            key={dname}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              console.log('💬 [AcceptedRequestsView] Clic sur chat:', { requestId: r.id, driverName: actualDriverName, passengerName, onOpenChat: !!onOpenChat });
                              if (onOpenChat) {
                                onOpenChat(r.id, actualDriverName, passengerName);
                              } else {
                                console.error('❌ [AcceptedRequestsView] onOpenChat n\'est pas défini');
                                alert('Erreur: Impossible d\'ouvrir le chat');
                              }
                            }}
                            onTouchStart={(e) => {
                              e.stopPropagation();
                            }}
                            style={{
                              background: hasMsgs ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              border: 'none',
                              color: 'white',
                              width: '40px',
                              height: '40px',
                              minWidth: '40px',
                              minHeight: '40px',
                              borderRadius: '50%',
                              cursor: 'pointer',
                              fontSize: '1.2rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: hasMsgs ? '0 3px 10px rgba(239, 68, 68, 0.4)' : '0 2px 6px rgba(102, 126, 234, 0.3)',
                              transition: 'all 0.2s ease',
                              zIndex: 10,
                              position: 'relative',
                              WebkitTapHighlightColor: 'transparent',
                            }}
                            title={hasMsgs ? "Ouvrir le chat (messages disponibles)" : "Ouvrir le chat"}
                          >
                            💬
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
                <div style={{ minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:16 }}>{r.name || r.passengerName}</div>
                  <div style={{ fontSize:13, color:'#4b5563', lineHeight:1.4, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    {shortAddress(r)}
                  </div>
                  <div style={{ fontSize:12, color:'#6b7280', lineHeight:1.3 }}>
                    {formatDate(r.meetingAt)}
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', justifyContent:'flex-end' }}>
                  {/* Conducteurs avec icône voiture */}
                  <span style={{ display:'inline-flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                    {(() => {
                      const requestName = r.name || r.passengerName || 'Sans nom';
                      const requestId = r.id || 'Sans ID';
                      console.log(`🔍 [AcceptedRequestsView] Demande complète: ${requestName} (${requestId})`);
                      console.log(`   seatsAssigned: ${r.seatsAssigned || 0}`);
                      console.log(`   seatsNeeded: ${r.seatsNeeded || 1}`);
                      console.log(`   seatsRemaining: ${r.seatsRemaining || 0}`);
                      console.log(`   assigned: ${r.assigned}`);
                      console.log(`   drivers keys: ${r.drivers ? Object.keys(r.drivers).join(', ') : 'Aucun'}`);
                      
                      const drivers = r.drivers ? Object.keys(r.drivers) : (r.driverName ? [r.driverName] : []);
                      console.log(`🔍 [AcceptedRequestsView] Liste des conducteurs: [${drivers.join(', ')}]`);
                      
                      // Afficher tous les conducteurs sans limitation
                      return (
                        <>
                          {drivers.map((dname) => {
                            // Normaliser la clé pour accéder aux données (compatibilité avec les noms contenant des caractères spéciaux)
                            const normalizeKey = (name) => String(name || '').replace(/[.#$/\[\]]/g, '_').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
                            const driverKey = normalizeKey(dname);
                            const driverData = r.drivers ? (r.drivers[driverKey] || r.drivers[dname]) : null; // Support ancien format
                            const actualDriverName = driverData?.driverName || dname; // Utiliser le nom original si disponible
                            
                            // Récupérer l'icône du conducteur depuis les données de la demande
                            const driverIcon = (() => {
                              console.log('🔍 [WaitingListView] Recherche icône pour conducteur:', dname, 'clé normalisée:', driverKey, 'dans demande:', r.id);
                              console.log('🔍 [WaitingListView] Données drivers:', r.drivers);
                              
                              // Chercher l'icône dans les données du conducteur de cette demande
                              if (driverData && driverData.icon) {
                                console.log('✅ [WaitingListView] Icône trouvée dans drivers:', driverData.icon);
                                return driverData.icon;
                              }
                              console.log('⚠️ [WaitingListView] Pas d\'icône dans drivers, fallback vers profils locaux');
                              
                              // Fallback: chercher dans les profils locaux
                              try {
                                const profiles = JSON.parse(localStorage.getItem('cvtrg_profiles') || '[]');
                                const profile = profiles.find(p => p.name === dname);
                                const fallbackIcon = profile?.icon || '🚗';
                                console.log('🔄 [WaitingListView] Icône fallback:', fallbackIcon);
                                return fallbackIcon;
                              } catch {
                                console.log('❌ [WaitingListView] Erreur fallback, icône par défaut');
                                return '🚗';
                              }
                            })();
                            
                            // Ne pas afficher le ratio ici car le badge d'attention l'affiche déjà
                            // Juste afficher le nom du conducteur (utiliser le nom original)
                            console.log(`✅ [AcceptedRequestsView] Affichage conducteur: ${actualDriverName}`);
                            
                            return (
                              <span key={dname} style={{ background:'#f3f4f6', color:'#374151', borderRadius:9999, padding:'2px 6px', fontWeight:600, fontSize:'12px' }}>
                                {driverIcon} {actualDriverName}
                              </span>
                            );
                          })}
                        </>
                      );
                    })()}
                  </span>
                  {/* Places */}
                  {placesBadge(r)}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginTop:12 }}>
                {onCancelHelp && (
                  <button
                    type="button"
                    className="request-card__btn"
                    onClick={() => onCancelHelp(r)}
                    aria-label="Annuler mon aide"
                    style={{ background:'#ef4444', color:'#fff', border:'none', borderRadius:12, height:44, fontWeight:700 }}
                  >
                    ⛔ Annuler mon aide
                  </button>
                )}
                <button
                  type="button"
                  className="request-card__btn"
                  onClick={() => handleNavigationClick(r)}
                  aria-label="M'y rendre"
                  style={{ background:'#2563eb', color:'#fff', border:'none', borderRadius:12, height:44, fontWeight:700 }}
                >
                  🗺️ M'y rendre
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modale de navigation */}
      <NavigationModal
        isOpen={navigationModal.isOpen}
        onClose={() => setNavigationModal({ isOpen: false, address: "" })}
        address={navigationModal.address}
      />
    </>
  );
};

export default WaitingListView;
